import { memo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { useIntl } from 'react-intl'
import { getPcmTestimonialForOfferId } from 'constants/pcmTestimonials'
import Image from 'components/Common/Image'
import Sheet from 'components/utils/Sheet'

const Author = styled.div`
  display: grid;
  grid-template-columns: ${rem(44)} 1fr;
  gap: ${rem(12)};
  align-items: center;
`

const ExpertDetails = styled.div`
  min-width: 0;
`

const ExpertImageFrame = styled.div`
  width: ${rem(44)};
  height: ${rem(44)};
  padding: ${rem(2)};
  background: ${props => props.theme.palette.product.loyalty.gold.lightBackground};
  border: ${rem(2)} solid ${props => props.theme.palette.product.loyalty.gold.darkForeground};
  border-radius: ${props => props.theme.borderRadius.round};
`

const ExpertImage = styled(Image)`
  width: 100%;
  height: 100%;
  border-radius: ${props => props.theme.borderRadius.round};
  object-fit: cover;
`

const QuotePanel = styled.figure`
  position: relative;
  margin: 0;
  padding: ${rem(18)} ${rem(16)} ${rem(18)} ${rem(54)};
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
  border-left: ${rem(3)} solid ${props => props.theme.palette.product.loyalty.gold.darkForeground};
  border-radius: ${props => props.theme.borderRadius.S};

  &::before {
    content: '“';
    position: absolute;
    top: ${rem(12)};
    left: ${rem(16)};
    color: ${props => props.theme.palette.product.loyalty.gold.darkForeground};
    font-family: ${props => props.theme.font.primary.family};
    font-size: ${rem(52)};
    font-weight: ${props => props.theme.font.primary.weight.superBold};
    line-height: ${rem(40)};
  }
`

interface Props {
  offerId: string
  className?: string
}

function OfferPagePcmTestimonial({ offerId, className }: Props) {
  const intl = useIntl()
  const testimonial = getPcmTestimonialForOfferId(offerId)

  if (!testimonial) {
    return null
  }

  return (
    <Sheet className={className} background="normal-background" border="light-foreground" padding={18} borderRadius="M" shadow="bottom-small">
      <Group direction="vertical" gap={16}>
        <Heading variant="heading5">
          {intl.formatMessage({
            id: 'VQEsYv4X2AuAvjOKLH4C',
            defaultMessage: 'Our travel expert’s take',
            description: 'Heading for a testimonial section from a travel expert on the offer page.',
          })}
        </Heading>
        <QuotePanel>
          <BodyText variant="medium" wrap="pretty" italics>
            {testimonial.review}
          </BodyText>
        </QuotePanel>
        <Author>
          <ExpertImageFrame>
            <ExpertImage
              id={testimonial.imageId}
              alt={testimonial.name}
              width={44}
              height={44}
              fit="center"
            />
          </ExpertImageFrame>
          <ExpertDetails>
            <BodyText variant="large" weight="bold" wrap="truncate">
              {testimonial.name}
            </BodyText>
            <BodyText variant="medium" colour="neutral-two">
              {testimonial.title ?? intl.formatMessage({
                id: 'SFjamr0hU93fP9KlK8FO',
                defaultMessage: 'Partnerships Manager, Luxury Escapes',
                description: 'Job title for the travel expert shown alongside their offer page testimonial.',
              })}
            </BodyText>
          </ExpertDetails>
        </Author>
      </Group>
    </Sheet>
  )
}

export default memo(OfferPagePcmTestimonial)
